const API_BASE = '/api'

export interface UserInfo {
  id: string
  email: string
  displayName: string
  provider: string
}

export interface AuthData {
  user: UserInfo
  credits: {
    available: number
  }
  subscription: {
    active: boolean
    planId: string
    status: string
  } | null
}

export async function getMe(): Promise<AuthData | null> {
  try {
    const response = await fetch(`${API_BASE}/auth/me`, {
      credentials: 'include',
    })
    if (!response.ok) return null
    return response.json()
  } catch {
    return null
  }
}

export async function logout(): Promise<void> {
  await fetch(`${API_BASE}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  })
}

export function getLoginUrl(provider: 'google' | 'microsoft', redirect?: string): string {
  const params = redirect ? `?redirect=${encodeURIComponent(redirect)}` : ''
  return `${API_BASE}/auth/${provider}${params}`
}

export async function updateProfile(displayName: string): Promise<{ displayName: string }> {
  const response = await fetch(`${API_BASE}/auth/profile`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ displayName }),
  })
  if (!response.ok) {
    throw new Error('Failed to update profile')
  }
  return response.json()
}

export async function deleteAccount(): Promise<void> {
  const response = await fetch(`${API_BASE}/auth/account`, {
    method: 'DELETE',
    credentials: 'include',
  })
  if (!response.ok) {
    throw new Error('Failed to delete account')
  }
}

export async function exportMyData(): Promise<void> {
  const response = await fetch(`${API_BASE}/auth/export`, {
    credentials: 'include',
  })
  if (!response.ok) {
    throw new Error('Failed to export data')
  }
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `sign-this-data-export.json`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export interface UserForm {
  id: string
  title: string
  createdAt: string
  isActivated: boolean
}

export async function getMyForms(): Promise<UserForm[]> {
  const response = await fetch(`${API_BASE}/auth/my-forms`, {
    credentials: 'include',
  })
  if (!response.ok) return []
  const data = await response.json()
  return data.forms || []
}
